import { useQuery } from "@tanstack/react-query";
import { fetchMyPublishers, fetchPublisherContact, fetchPublisherGame } from "./service";
import { DefaultStaleTime } from "../core/react-query";
import { PublisherGameByGameId } from "../games/query-keys";
import { useContext } from "react";
import { CachedTokenContext } from "@seabrookstudios/auth";
import { usePublisherAndContacts } from "@seabrookstudios/pitch2table";

/**
 * @deprecated
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId?} [publisherId]
 */
export const PublisherContactKey = (publisherId) => ["publisher-contact", publisherId];

/**
 * Cache key for publishers I work for
 */
export const PublishersIWorkForKey = () => ["publishers-i-work-for"];

/**
 * @deprecated
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} publisherId
 * @param {import("@seabrookstudios/types").GameId} gameId
 */
export const usePublisherGame = (publisherId, gameId) => {
  const token = useContext(CachedTokenContext);

  return useQuery({
    queryKey: PublisherGameByGameId(publisherId, gameId),
    queryFn: async () => {
      const data = await fetchPublisherGame(token, publisherId, gameId);
      return data;
    },
    staleTime: DefaultStaleTime,
  });
};

export const usePublishersIWorkFor = () => {
  const token = useContext(CachedTokenContext);
  return useQuery({
    queryKey: PublishersIWorkForKey(),
    queryFn: async () => {
      const data = await fetchMyPublishers(token);
      return data.filter(Boolean);
    },
  });
};

/**
 * @deprecated
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} publisherId
 */
export const usePublisherContact = (publisherId) => {
  const token = useContext(CachedTokenContext);

  return useQuery({
    queryKey: PublisherContactKey(publisherId),
    queryFn: async () => {
      const data = await fetchPublisherContact(token, publisherId);
      return data;
    },
    staleTime: DefaultStaleTime,
  });
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 */
export const usePublisherName = (publisherId) => {
  const { data: pubAndContact = [], isLoading } = usePublisherAndContacts(publisherId);

  if (isLoading) {
    return undefined;
  }

  const [publisher] = pubAndContact;

  if (!publisher) {
    return undefined;
  }

  return publisher.name;
};

/**
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId|undefined} publisherId
 */
export const usePublisherNameOrUnlinked = (publisherId) => {
  const publisherName = usePublisherName(publisherId);

  if (!publisherId) {
    return "Unlinked";
  }

  return publisherName || "";
};

const MAX_PUBLISHER_NAMES = 10;

/**
 * Batch resolve publisher names for up to 10 publisher IDs (same order).
 * Used for charts; hook count is fixed so IDs array must be capped at 10.
 *
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId[]} publisherIds length ≤ 10
 * @returns {string[]} names in same order as publisherIds (empty string or "Unlinked" when missing)
 */
export const usePublisherNames = (publisherIds) => {
  const padded = [...(publisherIds || [])];
  while (padded.length < MAX_PUBLISHER_NAMES) padded.push(undefined);

  const id0 = padded[0];
  const id1 = padded[1];
  const id2 = padded[2];
  const id3 = padded[3];
  const id4 = padded[4];
  const id5 = padded[5];
  const id6 = padded[6];
  const id7 = padded[7];
  const id8 = padded[8];
  const id9 = padded[9];

  const r0 = usePublisherAndContacts(/** @type {*} */ (id0));
  const r1 = usePublisherAndContacts(/** @type {*} */ (id1));
  const r2 = usePublisherAndContacts(/** @type {*} */ (id2));
  const r3 = usePublisherAndContacts(/** @type {*} */ (id3));
  const r4 = usePublisherAndContacts(/** @type {*} */ (id4));
  const r5 = usePublisherAndContacts(/** @type {*} */ (id5));
  const r6 = usePublisherAndContacts(/** @type {*} */ (id6));
  const r7 = usePublisherAndContacts(/** @type {*} */ (id7));
  const r8 = usePublisherAndContacts(/** @type {*} */ (id8));
  const r9 = usePublisherAndContacts(/** @type {*} */ (id9));

  const results = [r0, r1, r2, r3, r4, r5, r6, r7, r8, r9];
  const ids = [id0, id1, id2, id3, id4, id5, id6, id7, id8, id9];

  return results
    .map((r, i) => {
      const id = ids[i];
      if (!id || id === "unset") return "Unlinked";
      const [publisher] = r.data || [];
      return publisher?.name ?? "";
    })
    .slice(0, publisherIds?.length ?? 0);
};
